import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Spinner } from 'react-bootstrap';
import { adminMerchantsAPI, adminKycAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', success: '#10B981', danger: '#EF4444',
  warning: '#F59E0B', radius: 14,
};

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{value || '—'}</div>
  </div>
);

const inputStyle = {
  width: '100%', padding: '9px 12px', fontSize: 13, color: C.ink,
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle = { fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 4, display: 'block' };

const Btn = ({ onClick, color = C.primary, children, disabled, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: color, color: '#fff', border: 'none', borderRadius: 8,
      padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, ...style,
    }}
  >{children}</button>
);

const SectionCard = ({ title, children }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius,
    padding: '20px 24px', marginBottom: 20,
  }}>
    <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</h4>
    {children}
  </div>
);

const AdminMerchantDetails = () => {
  const { id } = useParams();
  const [m, setM]           = useState(null);
  const [loading, setLoading] = useState(true);

  // Bank details state
  const [bankForm, setBankForm] = useState({
    bank_holder_name: '', bank_account_no: '', bank_ifsc: '',
    bank_name: '', bank_branch: '', account_type: 'savings',
  });
  const [bankEditing, setBankEditing] = useState(false);
  const [bankSaving, setBankSaving]   = useState(false);

  // Transaction limit state
  const [txnLimit, setTxnLimit]       = useState(6000);
  const [txnLimitSaving, setTxnLimitSaving] = useState(false);

  // KYC state
  const ALL_GROUPS = {
    'Document KYC':              ['aadhaar_front','aadhaar_back','pan_card','selfie','mobile','email_id','address'],
    'Business KYC':              ['gst_certificate','udyam_registration'],
    'Bank Verification':         ['cancelled_cheque','bank_statement','account_holder_name','account_number','ifsc_code'],
    'Website / App Verification':['website_url','product_details','ad_platform'],
    'Declarations':              ['digital_signature','terms_acceptance'],
  };
  const FIELD_LABELS = {
    aadhaar_front:'Aadhaar Card (Front)', aadhaar_back:'Aadhaar Card (Back)',
    pan_card:'PAN Card', selfie:'Selfie / Live Photo', mobile:'Mobile Number',
    email_id:'Email ID', address:'Current Address', gst_certificate:'GST Certificate (Optional)',
    udyam_registration:'Udyam Registration (Optional)', cancelled_cheque:'Cancelled Cheque',
    bank_statement:'Bank Statement (3–6 Months)', account_holder_name:'Account Holder Name',
    account_number:'Account Number', ifsc_code:'IFSC Code', website_url:'Website URL / App',
    product_details:'Product / Service Details', ad_platform:'Advertisement Platform',
    digital_signature:'Digital Signature', terms_acceptance:'Terms Acceptance',
  };
  const [kycPanel, setKycPanel]         = useState(false);   // show/hide panel
  const [kycFields, setKycFields]       = useState([]);       // selected fields
  const [kycCreating, setKycCreating]   = useState(false);
  const [kycResult, setKycResult]       = useState(null);     // { url, token }
  const [kycExisting, setKycExisting]   = useState(null);     // existing active form

  const toggleField = (key) =>
    setKycFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const selectGroup = (keys) =>
    setKycFields(prev => {
      const all = keys.every(k => prev.includes(k));
      return all ? prev.filter(k => !keys.includes(k)) : [...new Set([...prev, ...keys])];
    });

  const createKycForm = async () => {
    if (kycFields.length === 0) return toast.error('Select at least one field');
    setKycCreating(true);
    try {
      const { data } = await adminKycAPI.create({ mid: m.merchant_id || m.mid, fields: kycFields });
      setKycResult({ url: data.url, token: data.token });
      toast.success('KYC form created!');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create KYC form';
      if (err?.response?.data?.url) {
        setKycResult({ url: err.response.data.url });
        toast.warning('Active form already exists — URL shown below');
      } else {
        toast.error(msg);
      }
    } finally { setKycCreating(false); }
  };

  useEffect(() => {
    adminMerchantsAPI.show(id)
      .then(({ data }) => {
        const merchant = data?.data;
        setM(merchant);
        if (merchant) {
          setBankForm({
            bank_holder_name: merchant.bank_holder_name || '',
            bank_account_no:  merchant.bank_account_no  || '',
            bank_ifsc:        merchant.bank_ifsc         || '',
            bank_name:        merchant.bank_name         || '',
            bank_branch:      merchant.bank_branch       || '',
            account_type:     merchant.account_type      || 'savings',
          });
          setTxnLimit(merchant.transaction_limit ?? 6000);
        }
      })
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  }, [id]);

  const hasBankDetails = m && (m.bank_account_no || m.bank_holder_name || m.bank_ifsc);

  const saveBankDetails = async () => {
    setBankSaving(true);
    try {
      await adminMerchantsAPI.updateBankDetails(id, bankForm);
      toast.success('Bank details saved');
      setBankEditing(false);
      setM(prev => ({ ...prev, ...bankForm }));
    } catch {
      toast.error('Failed to save bank details');
    } finally { setBankSaving(false); }
  };

  const deleteBankDetails = async () => {
    if (!window.confirm('Remove all bank details for this merchant?')) return;
    try {
      await adminMerchantsAPI.deleteBankDetails(id);
      toast.success('Bank details removed');
      setBankForm({ bank_holder_name: '', bank_account_no: '', bank_ifsc: '', bank_name: '', bank_branch: '', account_type: 'savings' });
      setM(prev => ({ ...prev, bank_holder_name: null, bank_account_no: null, bank_ifsc: null, bank_name: null, bank_branch: null, account_type: null }));
      setBankEditing(false);
    } catch {
      toast.error('Failed to remove bank details');
    }
  };

  const saveTxnLimit = async () => {
    setTxnLimitSaving(true);
    try {
      await adminMerchantsAPI.updateTxnLimit(id, txnLimit);
      toast.success('Transaction limit updated');
    } catch {
      toast.error('Failed to update limit');
    } finally { setTxnLimitSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spinner animation="border" /></div>;
  if (!m) return <p style={{ padding: 24 }}>Merchant not found.</p>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Link to="/admin/merchants" style={{ fontSize: 13, color: C.primary, display: 'inline-block', marginBottom: 20 }}>
        ← Back to Merchants
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── Left column ── */}
        <div>
          <SectionCard title="Account Information">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <Field label="Username"   value={m.username} />
              <Field label="Email"      value={m.email} />
              <Field label="Phone"      value={m.phone} />
              <Field label="MID"        value={<code style={{ fontSize: 12 }}>{m.merchant_id}</code>} />
              <Field label="Role"       value={m.role || 'partner'} />
              <Field label="Wallet"     value={`₹ ${Number(m.wallet_balance || 0).toFixed(2)}`} />
              <Field label="Approval"   value={<span className={`a1x-badge a1x-badge-${m.status}`}>{m.status}</span>} />
              <Field label="Account"    value={<span className={`a1x-badge a1x-badge-${m.account_status}`}>{m.account_status}</span>} />
              <Field label="API Key Issued" value={m.api_key_issued_at ? new Date(m.api_key_issued_at).toLocaleString() : '—'} />
              <Field label="Joined"     value={m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Link to={`/admin/transactions?user_id=${m.id}`} className="btn btn-sm btn-outline-primary">Transactions</Link>
              <Link to={`/admin/settlements?user_id=${m.id}`} className="btn btn-sm btn-outline-primary">Settlements</Link>
            </div>
          </SectionCard>

          {/* ── Settlement Config (already exists in user detail page, mirror here) ── */}
          <SectionCard title="Settlement Configuration">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Settlement Cycle" value={m.settlement_cycle?.toUpperCase() || 'T+3'} />
              <Field label="Settlement %" value={`${m.settlement_percentage ?? 3}%`} />
            </div>
          </SectionCard>

          {/* ── Transaction Limit (admin only, hidden from merchant) ── */}
          <SectionCard title="Transaction Limit (Admin Only)">
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Maximum amount per single payment. Not visible to merchant. Default: ₹6,000.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Limit (₹)</label>
                <input
                  type="number"
                  min="1"
                  style={inputStyle}
                  value={txnLimit}
                  onChange={e => setTxnLimit(e.target.value)}
                />
              </div>
              <Btn
                onClick={saveTxnLimit}
                disabled={txnLimitSaving}
                style={{ marginTop: 20, whiteSpace: 'nowrap' }}
              >
                {txnLimitSaving ? 'Saving…' : 'Save Limit'}
              </Btn>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: C.body }}>
              Current limit: <strong>₹{Number(txnLimit).toLocaleString('en-IN')}</strong> per transaction
            </div>
          </SectionCard>
        </div>

        {/* ── Right column ── */}
        <div>
          <SectionCard title="Bank Details">
            {!bankEditing ? (
              <>
                {hasBankDetails ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    <Field label="Account Holder" value={m.bank_holder_name} />
                    <Field label="Account Number" value={m.bank_account_no} />
                    <Field label="IFSC Code"      value={m.bank_ifsc} />
                    <Field label="Bank Name"      value={m.bank_name} />
                    <Field label="Branch"         value={m.bank_branch} />
                    <Field label="Account Type"   value={m.account_type} />
                  </div>
                ) : (
                  <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>No bank details added yet.</p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn onClick={() => setBankEditing(true)}>
                    {hasBankDetails ? 'Edit Details' : 'Add Bank Details'}
                  </Btn>
                  {hasBankDetails && (
                    <Btn onClick={deleteBankDetails} color={C.danger}>Remove</Btn>
                  )}
                </div>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { key: 'bank_holder_name', label: 'Account Holder Name *' },
                  { key: 'bank_account_no',  label: 'Account Number *' },
                  { key: 'bank_ifsc',        label: 'IFSC Code *' },
                  { key: 'bank_name',        label: 'Bank Name' },
                  { key: 'bank_branch',      label: 'Branch' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    <input
                      style={inputStyle}
                      value={bankForm[key]}
                      onChange={e => setBankForm(p => ({ ...p, [key]: e.target.value }))}
                      placeholder={label.replace(' *', '')}
                    />
                  </div>
                ))}
                <div>
                  <label style={labelStyle}>Account Type</label>
                  <select
                    style={inputStyle}
                    value={bankForm.account_type}
                    onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}
                  >
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                  </select>
                </div>

                <div style={{ gridColumn: '1/-1', display: 'flex', gap: 8, marginTop: 4 }}>
                  <Btn onClick={saveBankDetails} disabled={bankSaving}>
                    {bankSaving ? 'Saving…' : 'Save Details'}
                  </Btn>
                  <Btn onClick={() => setBankEditing(false)} color={C.muted}>Cancel</Btn>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── KYC Form Builder ── */}
      <div style={{ marginTop: 24 }}>
        <SectionCard title="KYC Form">
          {!kycPanel && !kycResult && (
            <div>
              <p style={{ fontSize: 13, color: C.body, margin: '0 0 14px' }}>
                Create a unique KYC link for this merchant. They will log in with their email &amp; password to fill it.
              </p>
              <Btn onClick={() => setKycPanel(true)}>Create KYC Form</Btn>
            </div>
          )}

          {kycPanel && !kycResult && (
            <div>
              <p style={{ fontSize: 13, color: C.body, margin: '0 0 16px' }}>
                Select the fields you want this merchant to fill:
              </p>

              {Object.entries(ALL_GROUPS).map(([group, keys]) => (
                <div key={group} style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.primary }}>
                      {group}
                    </span>
                    <button
                      onClick={() => selectGroup(keys)}
                      style={{ fontSize: 11, color: C.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                    >
                      {keys.every(k => kycFields.includes(k)) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {keys.map(key => (
                      <label key={key} style={{
                        display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                        background: kycFields.includes(key) ? '#f1ebff' : C.bg,
                        border: `1.5px solid ${kycFields.includes(key) ? C.primary : C.border}`,
                        borderRadius: 8, padding: '6px 12px', fontSize: 13, userSelect: 'none',
                      }}>
                        <input
                          type="checkbox"
                          checked={kycFields.includes(key)}
                          onChange={() => toggleField(key)}
                          style={{ accentColor: C.primary }}
                        />
                        {FIELD_LABELS[key]}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <Btn onClick={createKycForm} disabled={kycCreating || kycFields.length === 0}>
                  {kycCreating ? 'Creating…' : `Generate KYC Link (${kycFields.length} fields)`}
                </Btn>
                <Btn onClick={() => { setKycPanel(false); setKycFields([]); }} color={C.muted}>Cancel</Btn>
              </div>
            </div>
          )}

          {kycResult && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: C.success }}>KYC link generated successfully</span>
              </div>
              <p style={{ fontSize: 13, color: C.body, margin: '0 0 10px' }}>
                Share this link with the merchant. They must log in with their registered email &amp; password:
              </p>
              <div style={{
                background: '#F0FDF4', border: `1px solid #BBF7D0`, borderRadius: 10,
                padding: '12px 16px', fontFamily: 'monospace', fontSize: 13,
                wordBreak: 'break-all', color: '#065F46', marginBottom: 12,
              }}>
                {kycResult.url}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => { navigator.clipboard.writeText(kycResult.url); toast.success('Link copied!'); }} color={C.success}>
                  Copy Link
                </Btn>
                <Btn onClick={() => { setKycResult(null); setKycPanel(false); setKycFields([]); }} color={C.muted}>
                  Create Another
                </Btn>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

    </div>
  );
};

export default AdminMerchantDetails;
